import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { Money } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import CaptiveDialog from 'features/rdc/components/CaptiveDialog/CaptiveDialog';
import { determineDefaultAccount } from 'features/rdc/helpers/determineDefaultAccount.helpers';
import useRDCNavigator from 'features/rdc/hooks/useRDCNavigator';
import { AmountMode } from 'features/rdc/RDCSearchParams';
import { AccountSelector } from 'features/rdc/screens/AccountSelctor/AccountSelector';
import { RDCAccount } from 'features/rdc/screens/Amount/hooks/useFetchAccountDetails';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageRequireSource, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FDICDisclaimer from 'shared/components/FDICDisclaimer/FDICDisclaimer';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import TransferField from 'shared/components/vxl/fields/TransferField/TransferField';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isMobileBrowser, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { applyKeyToAmount, parseToFloat } from 'shared/utils/numberUtils';
import { formatCurrency } from 'shared/utils/string/currency';
import { Maybe } from 'shared/utils/types';

export interface AmountAndAccountSelectorProps {
  accounts: RDCAccount[];
  accountSelected: (id: string) => void;
  sessionID: string;
  preSelectedAccount: string;
  isLoadingInBackground: boolean;
  maxDeposit?: Money;
  depositFlowId: string;
  mode: AmountMode;
}

export const AmountAndAccountSelector = ({
  accounts,
  accountSelected,
  sessionID,
  preSelectedAccount,
  isLoadingInBackground,
  maxDeposit,
  depositFlowId,
  mode,
}: AmountAndAccountSelectorProps) => {
  const router = useRouter();
  const maxTransferAmount = 9999999.99;
  const { t: translatedText } = useTranslation('rdc', {
    keyPrefix: 'accountSelection',
  });
  const { bottom } = useSafeAreaInsets();
  const { amount } = useLocalSearchParams<{ amount: string }>();
  const [transferAmount, setTransferAmount] = useState<string>(
    amount ?? '0.00',
  );

  const { amount: maxDepositAmount } = maxDeposit ?? {};
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const initialAccount = determineDefaultAccount(accounts, preSelectedAccount);
  /*As we set the default account ourselves we also need to generate our own session.
  we use this flag to ensure no duplicate calls are made while still keeping our sessionID in sync with the current selected account*/
  const [defaultAccountCreated, setDefaultAccountCreated] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<Maybe<RDCAccount>>(initialAccount);
  const [modalVisible, setModalVisible] = useState(false);
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false);
  const { finish } = useRDCNavigator();
  const companyLogo =
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require('assets/images/vxl/logos/varo-logo-square.png') as ImageRequireSource;

  const logCheckDepositAccountSelectedEvent = usePressableAnalytics({
    analyticsId: 'checkDepositAccountSelected',
    customAttributes: {
      account_type: selectedAccount?.product.toLowerCase() ?? '',
    },
  });

  useEffect(() => {
    if (initialAccount && !defaultAccountCreated) {
      setDefaultAccountCreated(true);
      //if the initial account id and the preselected account match then the session created from previous screen is okay and no need to update
      if (initialAccount.id !== preSelectedAccount) {
        accountSelected(initialAccount.id);
      }
    }
  }, [
    initialAccount,
    defaultAccountCreated,
    accountSelected,
    preSelectedAccount,
  ]);

  useEffect(() => {
    logCheckDepositAccountSelectedEvent();
  }, [logCheckDepositAccountSelectedEvent, selectedAccount]);

  const handleBackButtonAction = () => {
    setModalVisible(true);
  };

  useBackHandler(() => {
    handleBackButtonAction();
  });

  const optionsForAccountSelector = createOptionsForAccountSelector(accounts);

  const handleAccountSelectionAction = () => {
    if (optionsForAccountSelector.length > 1) {
      setBottomSheetVisible(true);
      bottomSheetRef.current?.present();
    }
  };

  const handleAccountSelected = (accountID: string) => {
    setBottomSheetVisible(false);
    bottomSheetRef.current?.close();
    if (accountID !== selectedAccount?.id) {
      const selectedAccount = accounts.find(
        (account) => account.id === accountID,
      );
      setSelectedAccount(selectedAccount);
      accountSelected(accountID);
    }
  };

  const handleNumPadAction = (key: string) => {
    const newAmount = applyKeyToAmount(transferAmount, key);
    if (newAmount !== undefined && newAmount !== transferAmount) {
      setTransferAmount(newAmount);
    }
  };

  const handleNextAction = () => {
    logCheckDepositAmountEnteredEvent();
    router.replace({
      pathname: mode === 'edit-amount' ? './review' : `./capture-front`,
      params: {
        session: sessionID,
        account: selectedAccount?.id,
        amount: transferAmount,
        depositFlowId,
      },
    });
  };

  const selectedAmount = parseToFloat(transferAmount);
  const limit = parseToFloat(maxDepositAmount ?? '0.00');

  const withinMaxLimit = selectedAmount <= Math.min(limit, maxTransferAmount);

  // Check conditions to enable the submit button
  const shouldEnableSubmitButton = selectedAmount > 0 && withinMaxLimit;

  const { transferFieldPrimaryText, transferFieldSecondaryText } = useMemo<{
    transferFieldPrimaryText: string;
    transferFieldSecondaryText?: string;
  }>(() => {
    if (!selectedAccount) {
      return {
        transferFieldPrimaryText: translatedText('selectAnAccount'),
      };
    }

    return {
      transferFieldPrimaryText: `${selectedAccount.nickname} • ${selectedAccount.accountNumberLast4}`,
      transferFieldSecondaryText: formatCurrency(
        selectedAccount.displayBalance?.amount.amount ?? '0.00',
        true,
      ),
    };
  }, [selectedAccount, translatedText]);

  // Analytics

  const logCheckDepositAmountEnteredEvent = usePressableAnalytics({
    analyticsId: 'checkDepositAmountEntered',
    customAttributes: { amount: transferAmount },
  });

  if (isWeb) {
    return <Redirect href="/move-money/check-deposit" />;
  }
  return (
    <View style={[styles.mainContainer, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          gestureEnabled: false,
          title: translatedText('headerTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={handleBackButtonAction}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isLoadingInBackground}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={null}
      />
      <View>
        <TransferField
          primaryText={transferFieldPrimaryText}
          secondaryText={transferFieldSecondaryText}
          labelText={translatedText('labelTextTo')}
          avatarAccessory={{
            name: VXLComponentId.ICON_AVATAR,
            id: 0,
            props: {
              iconAccessibilityLabel: 'logo',
              imageSource: companyLogo,
              variant: 'MD',
            },
          }}
          onPress={handleAccountSelectionAction}
          showChevron={!(optionsForAccountSelector.length === 1)}
          isCollapsed={!bottomSheetVisible}
        />
      </View>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <View style={styles.centeredContent}>
          <View
            style={
              isMobileBrowser
                ? styles.amountMainWrapperMobileWeb
                : styles.amountMainWrapper
            }
          >
            <ExpressiveXXLText>
              {formatCurrency(transferAmount, true)}
            </ExpressiveXXLText>
          </View>

          <View style={styles.transferLimitContainer}>
            {maxDepositAmount ? (
              <BodyXSText
                style={{
                  color: withinMaxLimit
                    ? theme.color.text.primary
                    : theme.color.status.negative,
                }}
              >
                {translatedText('transferLimit') +
                  formatCurrency(maxDepositAmount, true)}
              </BodyXSText>
            ) : null}
          </View>

          <FDICDisclaimer />
        </View>
      </ScrollView>
      <View style={styles.numPadContainer}>
        <NumberPad
          submitButtonLabel={translatedText('buttonLabel')}
          handleButtonPress={handleNumPadAction}
          handleSubmit={handleNextAction}
          disabled={false}
          submitDisabled={!shouldEnableSubmitButton}
        />
      </View>
      <BottomSheet ref={bottomSheetRef}>
        <AccountSelector
          handleAccountSelected={handleAccountSelected}
          options={optionsForAccountSelector}
        />
      </BottomSheet>
      <CaptiveDialog
        isDialogVisible={modalVisible}
        setModalVisible={setModalVisible}
        handlePrimaryAction={() => {
          finish();
        }}
        handleSecondaryAction={(value) => setModalVisible(value)}
      />
    </View>
  );
};

const createOptionsForAccountSelector = (
  accounts: RDCAccount[],
): {
  labelText: string;
  secondaryText: string;
  value: string;
}[] => {
  return accounts.map((account) => {
    return {
      labelText: `${account.nickname} • ${account.accountNumberLast4}`,
      secondaryText: formatCurrency(
        // This is a safe casting as all implementations of IInternalBankingAccount use display balance
        account.displayBalance?.amount.amount ?? '0.00',
        true,
      ),
      value: account.id,
    };
  });
};

const styles = StyleSheet.create({
  amountMainWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingBottom: isWeb ? theme.spacing.m : 0,
  },
  amountMainWrapperMobileWeb: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  centeredContent: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
  },
  mainContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  numPadContainer: {
    paddingHorizontal: theme.spacing.s,
  },

  scrollContainer: {
    flexGrow: 1,
  },
  transferLimitContainer: {
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginHorizontal: theme.spacing.m,
  },
});
